import React, { useEffect, useState, forwardRef } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Box,
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02"];

export const SalesReport = ({ reportRange, rawData }) => {

    const theme = useTheme();
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [orderSummary, setOrderSummary] = useState({});
    const [paymentSummary, setPaymentSummary] = useState({});
    const [cashDropTotal, setCashDropTotal] = useState(0);
    const [paidOutTotal, setPaidOutTotal] = useState(0);
    const [salesSummary, setSalesSummary] = useState({});

    useEffect(() => {
        // Correctly handle missing rawData to prevent errors
        if (!rawData) {
            console.log("Didn't find any Data to move Forward...");
            setChartData([]);
            setPieData([]);
            setOrderSummary({});
            setPaymentSummary({});
            setCashDropTotal(0);
            setPaidOutTotal(0);
            setSalesSummary({});
            return;
        }

        const hourlySales = {};
        const paymentMap = { "Cash": 0.0, "Credit Card": 0.0, "Gift Card": 0.0, "Online": 0.0, Other: 0.0 };
        let tipTotal = 0;
        let completed = 0;
        const safeRound = (num) => parseFloat(num.toFixed(2));

        // rawData. ->. dailyData. orders

        // Use optional chaining for safe access
        var voided = 0;
        const orderTypeMap = { "In-store": 0, Pickup: 0, Kiosk: 0, Online: 0, Other: 0 };

        console.log(rawData.orders);
        console.log(rawData.dailyData);
        // Use a null check on the loop itself
        for (let order of rawData.orders || []) {
            if (order.oStatus === 5) {
                voided += 1;
                continue;
            }
            const hour = new Date(order.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            hourlySales[hour] = (hourlySales[hour] || 0) + (order.total ?? 0);
            tipTotal += order.Tip ?? 0;
            completed++;

            // Use a null check on the inner loop
            for (let payment of order.payment || []) {
                const amount = parseFloat(payment.amount) ?? 0.0;

                switch (payment.payType) {
                    // Accumulate amounts first
                    case 0: paymentMap["Cash"] += amount; break;
                    case 1: paymentMap["Gift Card"] += amount; break;
                    case 2: paymentMap["Online"] += amount; break;
                    case 3: paymentMap["Credit Card"] += amount; break;
                    default: paymentMap["Other"] += amount;
                }
            }


            const typeName =
                order.type === 0 ? "In-store" :
                    order.type === 1 ? "Pickup" :
                        order.type === 2 ? "Kiosk" :
                            order.type === 3 ? "Online" : "Other";
            orderTypeMap[typeName] = (orderTypeMap[typeName] || 0) + 1;
        }

        paymentMap["Cash"] = safeRound(paymentMap["Cash"]);
        paymentMap["Gift Card"] = safeRound(paymentMap["Gift Card"]);
        paymentMap["Online"] = safeRound(paymentMap["Online"]);
        paymentMap["Credit Card"] = safeRound(paymentMap["Credit Card"]);
        paymentMap["Other"] = safeRound(paymentMap["Other"]);

        const cashDropSum = (rawData.dailyData.cashDrops || []).reduce((acc, drop) => acc + (drop.amount ?? 0), 0);
        const paidOutSum = (rawData.dailyData.paidOuts || []).reduce((acc, payout) => acc + (payout.amount ?? 0), 0);

        setChartData(Object.entries(hourlySales).map(([date, total]) => ({ date, total })));
        setPieData(Object.entries(paymentMap).map(([name, value]) => ({ name, value })));
        setOrderSummary({ ...orderTypeMap, Completed: completed, Void: voided });
        setPaymentSummary(paymentMap);
        setCashDropTotal(cashDropSum);
        setPaidOutTotal(paidOutSum);

        const expectedCash = (rawData.dailyData.openingCash ?? 0) + (paymentMap["Cash"] ?? 0) - cashDropSum - paidOutSum;
        const difference = (rawData.dailyData.tillCash ?? 0) - expectedCash;

        setSalesSummary({
            subTotal: rawData.dailyData.totalSales ?? 0,
            tax: safeRound((rawData.dailyData.totalSales)*0.95) ?? 0, // Assuming a totalTax property might exist
            total: rawData.dailyData.totalSales ?? 0,
            tips: tipTotal,
            openingCash: rawData.dailyData.openingCash ?? 0,
            cashCount: rawData.dailyData.tillCash ?? 0,
            overShort: difference.toFixed(2)
        });

    }, [reportRange, rawData]);


    return (
        <Container sx={{ mt: 4, mb: 4 }} >
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backgroundColor: "#f5faff" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Overview
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                       
                        <Box sx={{ width: "100%", height: 300 }}> 
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    
                                    <XAxis dataKey="date" tick={{fontSize: 12}} />
                                    <YAxis width={40} tick={{fontSize: 12}} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: "12px" }} />
                                    <Line type="monotone" dataKey="total" stroke={theme.palette.primary.main} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PieChart width={300} height={250}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#fff8e1" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order Type</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(orderSummary).map(([type, count]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        <TableCell align="right">{count}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#e1f5fe" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Payment Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Payment Type</TableCell>
                                    <TableCell align="right">Amount ($)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(paymentSummary).map(([type, amount]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        {/* Use optional chaining for safe number formatting */}
                                        <TableCell align="right">${amount?.toFixed(2) ?? '0.00'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <br />

                        <Typography variant="subtitle2">Total Cash Drops : ${cashDropTotal?.toFixed(2) ?? '0.00'}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Total Paid Outs : ${paidOutTotal?.toFixed(2) ?? '0.00'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mt: 3, backgroundColor: "#f3e5f5" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Summary
                </Typography>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>Sub Total</TableCell>
                            <TableCell align="right">${salesSummary.subTotal?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tax</TableCell>
                            <TableCell align="right">${salesSummary.tax?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Total Sale</TableCell>
                            <TableCell align="right">${salesSummary.total?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tips</TableCell>
                            <TableCell align="right">${salesSummary.tips?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Opening Cash</TableCell>
                            <TableCell align="right">${salesSummary.openingCash?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Cash Count</TableCell>
                            <TableCell align="right">${salesSummary.cashCount?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Over/Short</TableCell>
                            <TableCell align="right" sx={{ color: "error.main" }}>
                                ${salesSummary.overShort ?? '0.00'}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
        </Container>
    );
};